import { Card, Col, Row, Statistic, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';

/** `GET /admin/call-requests-stats` — caller-level direct-call fallback numbers. */
export interface CallRequestStats {
  requestsStarted: number;
  requestsByStatus: Record<string, number>;
  ringsByEndReason: Record<string, number>;
  averageAttemptsToConnect: number | null;
  averageSecondsToAnsweredRing: number | null;
  fallbackRate: number | null;
  /** Rings that went to a dummy (placeholder) listener — timed, never answered (absent on older API builds). */
  dummyAttempts?: number;
}

/** Share of `part` in `whole`, as "12%" (or "—" with nothing to divide). */
export function pct(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';
}

/**
 * Direct calls, counted the way the CALLER sees them: one request each, however
 * many buddies it rang. The per-ring numbers (per listener) are on the charts
 * below, unchanged.
 */
export function CallRequestStatsCard({ days }: { days: number }) {
  const q = useQuery({
    queryKey: ['analytics', 'call-requests', days],
    queryFn: async () =>
      (await api.get<CallRequestStats>('/admin/call-requests-stats', { params: { days } })).data,
    refetchInterval: 60_000,
  });
  const s = q.data;
  const by = s?.requestsByStatus ?? {};
  const connected = (by.connected ?? 0) + (by.completed ?? 0);
  const rings = s?.ringsByEndReason ?? {};
  return (
    <Card title={`Direct calls with fallback — last ${days} days`} loading={q.isLoading}>
      {s && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Statistic title="Calls started" value={s.requestsStarted} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Connected" value={connected} suffix={`(${pct(connected, s.requestsStarted)})`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="No buddy answered" value={(by.exhausted ?? 0) + (by.expired ?? 0)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Cancelled by caller" value={by.cancelled ?? 0} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Rings to connect (avg)"
                value={s.averageAttemptsToConnect ?? '—'}
                precision={s.averageAttemptsToConnect == null ? undefined : 1}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Seconds to answered ring (avg)"
                value={s.averageSecondsToAnsweredRing ?? '—'}
                precision={s.averageSecondsToAnsweredRing == null ? undefined : 0}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Answered by a fallback buddy"
                value={s.fallbackRate == null ? '—' : `${Math.round(s.fallbackRate * 100)}%`}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Rings declined / not answered" value={`${rings.declined ?? 0} / ${rings.no_answer ?? 0}`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Dummy listener attempts" value={s.dummyAttempts ?? 0} />
            </Col>
          </Row>
          <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
            One call = one caller tap, however many buddies it rang. Skipped buddies (offline or busy
            at their turn) are never rung and are not counted as rings.
          </Typography.Paragraph>
        </>
      )}
    </Card>
  );
}
